import { CropNames, GenericImage, PictureBreakpoints } from './Image';

export type GeneratedImage = {
  isGenerated: boolean;
  imageData: GenericImage;
  pictureBreakpoints: PictureBreakpoints[];
};

/**
 * @name GenericImageGenerator
 *
 * @description Creates the Generic Image object
 * Returns object breakpointWidths, srcset and imageData.
 *
 * @param {GenericImage} genericImageObject
 * @param {string[]} excludedMqs pass one array of mqs as argument if want to exclude specific mq from the srcset. E.g: ['mq1', 'mq3']
 * @param {string} ratio if needed to force the image ratio
 * @returns {object} Generic Image Data object ready to use.
 */

export default class GenericImageGenerator {
  private imgSrcPlaceholder = '';
  private excludedMqs: string[];
  //FIXME: Importing this variables from the WB breaks the standalone build
  private MEDIA_QUERY_SIZES = {
    mq1: 320,
    mq2: 480,
    mq3: 768,
    mq4: 1024,
    mq5: 1280,
    mq6: 1440,
  };

  // if a secondary crop set is provided and active, this will reference them to the needed mq values
  private MEDIA_QUERY_SIZES_FOR_SECOND_CROP_SET = {
    mq1wide: 320,
    mq2wide: 480,
    mq3wide: 768,
    mq4wide: 1024,
    mq5wide: 1280,
    mq6wide: 1440,
  };

  public srcset: string;
  public imageData: GenericImage;
  public breakpointWidths: Record<string, number>;
  public pictureBreakpoints: PictureBreakpoints[];
  public isGenerated = false;

  constructor(
    genericImageObject: GenericImage,
    excludedMqs: string[] = [],
    ratio: string | null = null,
    mqCropMapping = false,
  ) {
    this.breakpointWidths = mqCropMapping
      ? this.MEDIA_QUERY_SIZES_FOR_SECOND_CROP_SET
      : this.MEDIA_QUERY_SIZES;

    this.imgSrcPlaceholder = genericImageObject.source;
    this.imageData = this.replaceDefaultUrl(genericImageObject);
    this.excludedMqs = excludedMqs;
    this.srcset = this.generateSrcSet();

    this.forceImageRatio(ratio);
    this.pictureBreakpoints = this.generatePictureBreakpoints();
  }

  private forceImageRatio(ratio: string | null): void {
    if (ratio) {
      this.imageData.ratio = ratio;
    }
  }

  private replaceImageUrlMqPlaceholder(url = '', mq = 'mq2'): string {
    return url.replace('{mq}', mq);
  }

  private replaceDefaultUrl(genericImageObject: GenericImage) {
    const imageData: GenericImage = Object.assign({}, genericImageObject);

    if (genericImageObject) {
      // if sources exist, take the mq2 value
      if (imageData.sources?.mq2) {
        imageData.source = imageData.sources?.mq2;
      } else {
        imageData.source = this.replaceImageUrlMqPlaceholder(genericImageObject.source);
      }
    }

    return imageData;
  }

  private generateSrcSet() {
    const breakpoints = Object.keys(this.breakpointWidths);

    const srcset = breakpoints
      .filter(breakpoint => !this.excludedMqs.includes(breakpoint))
      .map(
        (breakpoint: string): string =>
          `${this.getImageSource(breakpoint)} ${this.breakpointWidths[breakpoint]}w`,
      )
      .join();

    this.isGenerated = true;

    return srcset;
  }

  private generatePictureBreakpoints() {
    // We only need picture breakpoints to use picture element when
    // ratio is different for different breakpoints, for this reason
    // picture breakpoints are generated ONLY when ratio is not provided
    // otherwise it will be an empty list
    if (!this.imageData.ratio) {
      return Object.keys(this.breakpointWidths)
        .map(breakpoint => {
          return {
            srcset: this.getImageSource(breakpoint),
            media: `(min-width: ${this.breakpointWidths[breakpoint]}px)`,
          };
        })
        .reverse();
    }
    return [];
  }

  private getImageSource(breakpoint: string): string {
    if (!this.imageData.sources) {
      return `${this.replaceImageUrlMqPlaceholder(this.imgSrcPlaceholder, breakpoint)}`;
    }
    return `${this.imageData.sources[breakpoint as CropNames]}`;
  }
}
